import React, { useEffect } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import toast from "react-hot-toast";
import { ReportPeriodFilter } from "../../components/Filters/ReportPeriodFilter";
import { TenantGateBanner, useTenantGate } from "../../components/ui";
import {
  BenefitsMetricsLoadingOverlay,
  BenefitsMetricsRefreshingIndicator,
} from "./components/BenefitsMetricsWidgets";
import { useBenefitsMetricsQueryDisplay } from "../../hooks/useBenefitsMetricsQueryDisplay";
import { useReportPeriod } from "../../hooks/useReportPeriod";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  AuditReportColumn,
  AuditReportGrain,
  AuditReportRow,
  AuditReportTable,
} from "../../models/auditReports";
import {
  isReportDateRangeWithinLimit,
  reportDateRangeExceededMessage,
  toReportDateParam,
} from "../../utils/constants/report-date-range";
import {
  useExportAuditReportMutation,
  useGetAuditReportQuery,
} from "../../store/services/reports";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { downloadBlob, buildAnalyticsExportFilename } from "../../utils/functions/downloadBlob";
import { useTenantOptions } from "../../hooks/useTenantOptions";

const formatCell = (row: AuditReportRow, column: AuditReportColumn): string => {
  const value = row[column.key];
  if (value === undefined || value === null) {
    return "—";
  }
  if (column.kind === "decimal" && typeof value === "number") {
    // Sin sufijo: el `%` va en el encabezado de la columna ("% recurrencia"),
    // igual que en el CSV. Hardcodearlo acá rompía la premisa de que un reporte
    // nuevo del catálogo se ve bien sin tocar el front — la primera columna
    // decimal que no sea un porcentaje (un promedio, un monto) saldría "12,5%".
    return value.toLocaleString("es-AR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }
  if (column.kind === "integer" && typeof value === "number") {
    return value.toLocaleString("es-AR");
  }
  return String(value);
};

export interface AuditReportProps {
  /** Key del catálogo (`/bo/reports/audit/catalog`). */
  report: string;
  /** Título del selector, para poder pintarlo antes de que llegue la respuesta. */
  label: string;
  /** Granos que soporta el reporte, del catálogo. El primero es el default. */
  grains: AuditReportGrain[];
}

/**
 * Pantalla única de los reportes de auditoría (GTK-745).
 *
 * No sabe qué columnas tiene cada reporte: las toma de `columns` de la
 * respuesta, así que un reporte nuevo del catálogo se ve completo —tabla y
 * export— sin cambios acá.
 *
 * El selector de tipos lista métricas, no períodos: si dos reportes son la misma
 * métrica y solo cambia el bucket de tiempo, son un solo reporte con dos granos.
 * Y el grano no tiene control propio — lo define el botón de período, que setea
 * rango y agrupamiento juntos. Es lo único que hace que los botones tengan
 * sentido: "última semana" agrupado por mes daría una sola fila con una semana
 * de datos adentro.
 *
 * "Personalizado" es el cuarto botón y el único que muestra los selectores de
 * fecha. Mantiene el último agrupamiento usado, porque un rango a mano no dice
 * por sí solo si se quiere ver por semana o por mes; cuál quedó se muestra al
 * lado del botón de export y en el encabezado de la primera columna.
 */
export const AuditReport: React.FC<AuditReportProps> = ({ report, label, grains }) => {
  const gate = useTenantGate();
  const scope = useTenantScope();
  const { options: tenantOptions } = useTenantOptions();
  const tenantId = scope.tenantId;

  // La fila de períodos es la misma en los cuatro reportes: estado en el hook,
  // UI en <ReportPeriodFilter>. `grains` es lo único propio de este reporte —
  // filtra qué presets se ofrecen.
  const period = useReportPeriod(grains);
  const { grain, dateFrom, dateTo, rangeIsValid } = period;

  const [exportReport, { isLoading: isExporting }] = useExportAuditReportMutation();

  const params = {
    report,
    grain,
    tenantId: tenantId ?? "",
    dateFrom: toReportDateParam(dateFrom),
    dateTo: toReportDateParam(dateTo),
  };

  const query = useGetAuditReportQuery(params, {
    skip: scope.loading || !tenantId || !rangeIsValid,
  });

  useEffect(() => {
    if (query.isError) {
      toast.error(getApiErrorMessage(query.error) ?? "Error al cargar el reporte");
    }
  }, [query.isError, query.error]);

  const { metrics: table, isBlockingLoading, isRefreshing } = useBenefitsMetricsQueryDisplay<
    AuditReportTable | undefined
  >({
    data: query.data,
    currentData: query.currentData,
    isFetching: query.isFetching,
    // El grano y el rango entran en la clave de scope: al cambiarlos no queremos
    // pintar los números de la consulta anterior como si fueran los nuevos.
    scopeKey: `${tenantId ?? "none"}|${report}|${grain}|${params.dateFrom}|${params.dateTo}`,
  });

  const handleExport = async () => {
    if (!tenantId) return;

    if (!isReportDateRangeWithinLimit(dateFrom, dateTo)) {
      toast.error(reportDateRangeExceededMessage());
      return;
    }

    try {
      const result = await exportReport(params).unwrap();

      if (result.status === 204 || !result.blob || !result.filename) {
        toast("No hay datos para el rango seleccionado", { icon: "ℹ️" });
        return;
      }

      downloadBlob(result.blob, buildAnalyticsExportFilename(result.filename, tenantOptions));
      toast.success("Reporte exportado");
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) ?? "Error al exportar el reporte");
    }
  };

  if (!gate.loading && !gate.allowed) {
    return <TenantGateBanner action="ver este reporte" gate={gate} />;
  }

  const allColumns = table?.columns ?? [];
  const rows = table?.rows ?? [];

  /**
   * Total estimado del rango. Las columnas de calidad de dato no son métricas:
   * avisan que el período no se puede leer como definitivo. En un rango normal
   * dan 0 en todas las filas, y una columna de ceros es solo ruido — así que se
   * esconde y, cuando no es 0, se avisa arriba de la tabla donde se ve.
   */
  const dataQualityColumns = allColumns.filter((column) => column.data_quality);
  const estimatedTotal = dataQualityColumns.reduce(
    (total, column) =>
      total +
      rows.reduce((sum, row) => {
        const value = row[column.key];
        return sum + (typeof value === "number" ? value : 0);
      }, 0),
    0
  );
  const columns =
    estimatedTotal > 0 ? allColumns : allColumns.filter((column) => !column.data_quality);

  return (
    /* Sin card propia: la pantalla es una sola card grande (ver ReportsConfig).
       `relative overflow-hidden` se queda porque ancla el overlay de carga. */
    <div className="relative overflow-hidden">
      <BenefitsMetricsLoadingOverlay visible={isBlockingLoading} />

      <div className="mb-4 flex align-items-center gap-2">
        <h2 className="text-2xl font-semibold text-900 m-0">{table?.label ?? label}</h2>
        <BenefitsMetricsRefreshingIndicator visible={isRefreshing} />
      </div>

      <ReportPeriodFilter
        period={period}
        disabled={isExporting}
        idPrefix="audit-report"
        showGrain
      />

      {/* Solo el botón: el grano y el tope de 6 meses se explican con los
          botones de período, que es lo que los decide. */}
      <div className="flex flex-wrap align-items-center justify-content-end gap-3 mt-2 mb-4 pb-3 border-bottom-1 surface-border">
        <Button
          type="button"
          label="Exportar CSV"
          icon="pi pi-download"
          onClick={handleExport}
          // No se ata a `rows`: si la query de datos falla o todavía no volvió,
          // el export igual sirve, y el caso "no hay datos" lo resuelve el 204
          // del backend con un toast.
          disabled={isExporting || !rangeIsValid || !tenantId}
          loading={isExporting}
          data-testid="audit-report-export-button"
        />
      </div>

      {estimatedTotal > 0 && (
        <Message
          severity="warn"
          className="w-full justify-content-start mb-3"
          content={
            <div className="flex align-items-start gap-2">
              <i className="pi pi-exclamation-triangle mt-1" />
              <span>
                {estimatedTotal.toLocaleString("es-AR")} inscripciones de este rango tienen la
                fecha estimada a partir de la fecha de creación del evento, porque no tienen
                fecha de inscripción propia. Los períodos afectados sirven para el total, pero
                no se pueden leer como definitivos período por período. Mirá la columna «Fecha
                estimada» para ver cuáles.
              </span>
            </div>
          }
          data-testid="audit-report-estimated-warning"
        />
      )}

      <DataTable
        value={rows}
        dataKey={table?.period_column ?? "period"}
        emptyMessage="No hay datos todavía para el rango seleccionado"
        size="small"
        data-testid="audit-report-table"
      >
        {columns.map((column) => (
          <Column
            key={column.key}
            field={column.key}
            // Los encabezados son cortos para que el CSV se lea bien en Excel,
            // que no lleva ancho de columna. Lo que el encabezado corto no
            // alcanza a explicar viene en `description` y va acá como tooltip.
            header={
              column.description ? (
                <span title={column.description} className="cursor-help">
                  {column.header}
                  <i
                    className="pi pi-info-circle ml-1 text-color-secondary"
                    style={{ fontSize: "0.75rem" }}
                    aria-hidden="true"
                  />
                </span>
              ) : (
                column.header
              )
            }
            body={(row: AuditReportRow) => formatCell(row, column)}
            align={column.kind === "text" ? "left" : "right"}
          />
        ))}
      </DataTable>
    </div>
  );
};
